import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import {
  Plus, X, Package, ScanLine, ArrowDownToLine, ArrowUpFromLine, Boxes,
  Search, CheckCircle2, AlertTriangle, PackagePlus,
  Truck, ClipboardCheck, RotateCcw, Trash2, FileWarning,
} from "lucide-react";
import { toast } from "sonner";
import ScannerInput from "@/components/ScannerInput";

const EMPTY = { sku: "", tracking_code: "", barcode: "", descripcion: "", peso: 0, cbm: 0, unidades: 1, warehouse_id: "", ubicacion: "", estado: "received", notas: "" };
const STATES = ["received", "packaged", "dispatched"];
const STATE_COLORS = {
  received: "bg-blue-100 text-blue-700",
  packaged: "bg-amber-100 text-amber-700",
  dispatched: "bg-emerald-100 text-emerald-700",
};

const MODES = [
  { key: "receive_manifest", icon: ClipboardCheck, color: "emerald" },
  { key: "receive_direct", icon: PackagePlus, color: "blue" },
  { key: "prepare_exit", icon: Truck, color: "purple" },
];

export default function Inventory() {
  const { api, user } = useAuth();
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [q, setQ] = useState("");
  const [filterState, setFilterState] = useState("all");

  // Scanner mode (default receive_manifest)
  const [mode, setMode] = useState("receive_manifest");

  const load = () => api.get("/inventory").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/warehouses").then((r) => setWarehouses(r.data)).catch(() => {});
    if (["admin", "operaciones", "ventas", "sucursal"].includes(user.role)) {
      api.get("/clientes").then((r) => setClients(r.data)).catch(() => {});
    }
  }, []);

  const inbound = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, warehouse_id: form.warehouse_id || user.warehouse_id || warehouses[0]?.id || "" };
      if (!payload.warehouse_id) return toast.error(t("warehouse_required"));
      await api.post("/inventory/inbound", payload);
      toast.success(t("saved"));
      setShowForm(false);
      setForm(EMPTY);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const changeState = async (id, estado) => {
    try {
      await api.post(`/inventory/${id}/state`, { estado });
      toast.success(t("saved"));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const filtered = useMemo(() => {
    let out = rows;
    if (filterState !== "all") out = out.filter((r) => r.estado === filterState);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      out = out.filter((r) =>
        [r.sku, r.tracking_code, r.barcode, r.descripcion, r.ubicacion].filter(Boolean).some((v) => v.toLowerCase().includes(s))
      );
    }
    return out;
  }, [rows, q, filterState]);

  const stats = useMemo(() => ({
    received: rows.filter((r) => r.estado === "received").length,
    packaged: rows.filter((r) => r.estado === "packaged").length,
    dispatched: rows.filter((r) => r.estado === "dispatched").length,
    total: rows.length,
  }), [rows]);

  const inp = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600 focus:ring-2 focus:ring-red-100";
  const lbl = "text-[11px] font-semibold text-slate-600 uppercase mb-1 block";

  return (
    <div className="space-y-4" data-testid="inventory-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2">
            <Boxes className="w-5 h-5 text-red-600" /> {t("warehouse")} · {t("inventory")}
          </h2>
          <p className="text-sm text-slate-500">{stats.total} items</p>
        </div>
        <button onClick={() => { setForm({ ...EMPTY, warehouse_id: user.warehouse_id || warehouses[0]?.id || "" }); setShowForm(true); }} data-testid="new-inbound-btn" className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2">
          <ArrowDownToLine className="w-4 h-4" /> {t("inbound")}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {["received", "packaged", "dispatched"].map((st) => (
          <button key={st} onClick={() => setFilterState(filterState === st ? "all" : st)} className={`bg-white border rounded-xl p-4 text-start hover:shadow-md transition ${filterState === st ? "border-red-500 ring-2 ring-red-100" : "border-slate-200"}`} data-testid={`filter-${st}`}>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{t(`state_${st}`)}</div>
            <div className="text-2xl font-display font-bold text-slate-900 tabular-nums mt-1">{stats[st]}</div>
          </button>
        ))}
        <button onClick={() => setFilterState("all")} className={`bg-white border rounded-xl p-4 text-start hover:shadow-md transition ${filterState === "all" ? "border-red-500 ring-2 ring-red-100" : "border-slate-200"}`}>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{t("total")}</div>
          <div className="text-2xl font-display font-bold text-slate-900 tabular-nums mt-1">{stats.total}</div>
        </button>
      </div>

      {/* Scanner mode selector */}
      <div className="bg-white border border-slate-200 rounded-xl p-3">
        <div className="text-[11px] font-semibold uppercase text-slate-500 mb-2 flex items-center gap-2">
          <ScanLine className="w-3 h-3" /> {t("scanner_mode")}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {MODES.map(({ key, icon: Icon, color }) => (
            <button
              key={key}
              onClick={() => setMode(key)}
              data-testid={`mode-${key}`}
              className={`text-start p-3 rounded-lg border-2 transition ${mode === key ? `border-${color}-500 bg-${color}-50` : "border-slate-200 hover:border-slate-300"}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon className={`w-4 h-4 ${mode === key ? `text-${color}-600` : "text-slate-500"}`} />
                <span className={`text-sm font-semibold ${mode === key ? `text-${color}-800` : "text-slate-700"}`}>{t(`mode_${key}`)}</span>
              </div>
              <p className="text-[11px] text-slate-500">{t(`mode_${key}_desc`)}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Active scanner panel */}
      {mode === "receive_manifest" && <ReceiveManifestPanel api={api} user={user} t={t} onDone={load} />}
      {mode === "receive_direct" && <ReceiveDirectPanel api={api} user={user} clients={clients} warehouses={warehouses} t={t} onDone={load} />}
      {mode === "prepare_exit" && <PrepareExitPanel api={api} user={user} t={t} onDone={load} />}

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute start-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search") + "..."} className="ps-9 pe-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600 w-full max-w-md" />
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">Barcode</th>
                <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">SKU</th>
                <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">{t("description")}</th>
                <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">Tracking</th>
                <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">{t("location")}</th>
                <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">{t("weight_kg")}</th>
                <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">{t("state")}</th>
                {user.role === "admin" && <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">{t("who")}</th>}
                <th className="p-3 text-end text-[11px] uppercase tracking-wider font-semibold">{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="p-3 font-mono text-xs text-slate-700">{it.barcode}</td>
                  <td className="p-3">{it.sku || "—"}</td>
                  <td className="p-3 text-slate-800">{it.descripcion || "—"}</td>
                  <td className="p-3 font-mono text-xs text-slate-600">{it.tracking_code || "—"}</td>
                  <td className="p-3 text-slate-600">{it.ubicacion || "—"}</td>
                  <td className="p-3 tabular-nums">{it.peso || 0} kg</td>
                  <td className="p-3">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${STATE_COLORS[it.estado]}`}>{t(`state_${it.estado}`)}</span>
                  </td>
                  {user.role === "admin" && (
                    <td className="p-3 text-xs">
                      <div className="font-medium text-slate-700">{it.created_by_info?.nombre || it.created_by_name || "—"}</div>
                      <div className="text-[10px] text-slate-400 uppercase tracking-wider">{it.created_by_info?.role || ""}</div>
                      <div className="text-[10px] text-slate-500 tabular-nums">{new Date(it.created_at).toLocaleDateString()}</div>
                    </td>
                  )}
                  <td className="p-3 text-end">
                    <div className="flex gap-1 justify-end">
                      {STATES.filter((s) => s !== it.estado).map((s) => (
                        <button key={s} onClick={() => changeState(it.id, s)} className="text-[10px] font-semibold px-2 py-1 rounded border border-slate-200 hover:bg-slate-100" data-testid={`inv-${it.id}-${s}`}>
                          → {t(`state_${s}`)}
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={user.role === "admin" ? 9 : 8} className="p-8 text-center text-slate-400"><Package className="w-10 h-10 mx-auto opacity-30 mb-2" />{t("no_inventory")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <form onSubmit={inbound} className="bg-white rounded-xl w-full max-w-lg p-6 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="inbound-modal">
            <div className="flex justify-between items-center">
              <h3 className="font-display font-semibold text-lg flex items-center gap-2">
                <ArrowDownToLine className="w-5 h-5 text-red-600" /> {t("inbound")}
              </h3>
              <button type="button" onClick={() => setShowForm(false)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={lbl}>SKU</label>
                <input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className={inp} />
              </div>
              <div>
                <label className={lbl}>Tracking</label>
                <input value={form.tracking_code} onChange={(e) => setForm({ ...form, tracking_code: e.target.value })} className={inp} />
              </div>
              <div className="col-span-2">
                <label className={lbl}>{t("description")} *</label>
                <input required value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} className={inp} />
              </div>
              <div>
                <label className={lbl}>{t("weight_kg")}</label>
                <input type="number" step="0.1" value={form.peso} onChange={(e) => setForm({ ...form, peso: +e.target.value })} className={inp} />
              </div>
              <div>
                <label className={lbl}>CBM</label>
                <input type="number" step="0.01" value={form.cbm} onChange={(e) => setForm({ ...form, cbm: +e.target.value })} className={inp} />
              </div>
              <div>
                <label className={lbl}>{t("units")}</label>
                <input type="number" min={1} value={form.unidades} onChange={(e) => setForm({ ...form, unidades: +e.target.value })} className={inp} />
              </div>
              <div>
                <label className={lbl}>{t("location")}</label>
                <input value={form.ubicacion} onChange={(e) => setForm({ ...form, ubicacion: e.target.value })} placeholder="Rack A-12" className={inp} />
              </div>
              <div className="col-span-2">
                <label className={lbl}>{t("warehouse")} *</label>
                <select required value={form.warehouse_id} onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })} className={inp}>
                  <option value="">—</option>
                  {warehouses.map((w) => <option key={w.id} value={w.id}>{w.nombre}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className={lbl}>{t("notes")}</label>
                <input value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} className={inp} />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="flex-1 border border-slate-300 py-2.5 rounded-lg font-semibold text-sm">{t("cancel")}</button>
              <button type="submit" className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-lg font-semibold text-sm">{t("save")}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

/* =========================================================================
   RECEIVE WITH MANIFEST
   ========================================================================= */
function ReceiveManifestPanel({ api, user, t, onDone }) {
  const [session, setSession] = useState(null);
  const [manifestCode, setManifestCode] = useState("");
  const [nota, setNota] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [closeResult, setCloseResult] = useState(null);

  const startFromManifestBarcode = async (code) => {
    setBusy(true);
    try {
      const { data: m } = await api.get(`/manifests/scan/${encodeURIComponent(code)}`);
      const { data: s } = await api.post(`/manifests/${m.id}/reception/start`);
      setSession(s);
      setManifestCode(m.codigo);
      toast.success(`${t("reception_started")}: ${m.codigo}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("not_found"));
    } finally { setBusy(false); }
  };

  const scanPkg = async (code) => {
    if (!session) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/receptions/${session.id}/scan`, { tracking_code: code });
      setSession(data.session);
      if (data.status === "expected") toast.success(`✓ ${t("package_ok")}: ${code}`);
      else if (data.status === "extra") toast.warning(`⚠ ${t("package_extra")}: ${code}`);
      else if (data.status === "duplicate") toast.info(`${t("package_duplicate")}: ${code}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  const closeSession = async () => {
    if (!session) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/receptions/${session.id}/close`, {
        nota, photo_url: photoUrl, create_incidents: true,
      });
      setCloseResult(data);
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  const reset = () => {
    setSession(null); setManifestCode(""); setNota(""); setPhotoUrl(""); setCloseResult(null);
  };

  const expectedCodes = useMemo(() => new Set((session?.expected || []).map(e => e.tracking_code)), [session]);
  const scannedExpectedCodes = useMemo(() => new Set((session?.scanned || []).filter(s => s.status === "expected").map(s => s.tracking_code)), [session]);
  const extras = (session?.scanned || []).filter(s => s.status === "extra");
  const missingList = (session?.expected || []).filter(e => !scannedExpectedCodes.has(e.tracking_code));

  return (
    <div className="bg-slate-900 text-white rounded-xl p-4 space-y-4" data-testid="panel-receive-manifest">
      {/* Step 1: scan manifest */}
      {!session && (
        <>
          <div className="flex items-center gap-2 mb-2">
            <ClipboardCheck className="w-5 h-5 text-emerald-400" />
            <div>
              <div className="font-semibold text-sm">{t("step_1_scan_manifest")}</div>
              <div className="text-xs text-slate-400">{t("mode_receive_manifest_desc")}</div>
            </div>
          </div>
          <ScannerInput dark
            testIdPrefix="scan-manifest"
            placeholder={t("scan_manifest_barcode")}
            onScan={startFromManifestBarcode}
          />
        </>
      )}

      {/* Step 2: scan packages */}
      {session && !closeResult && (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-xs text-emerald-400 font-semibold uppercase tracking-wider">{t("step_2_scan_packages")}</div>
              <div className="font-mono font-bold text-lg">{manifestCode}</div>
            </div>
            <button onClick={reset} className="text-xs text-slate-300 hover:text-white flex items-center gap-1" data-testid="reset-reception">
              <RotateCcw className="w-3 h-3" /> {t("reset")}
            </button>
          </div>

          {/* Counters */}
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-slate-800 rounded-lg p-3">
              <div className="text-[10px] uppercase text-slate-400 font-semibold">{t("expected")}</div>
              <div className="text-2xl font-display font-bold tabular-nums">{session.expected.length}</div>
            </div>
            <div className="bg-emerald-900/40 border border-emerald-700/40 rounded-lg p-3">
              <div className="text-[10px] uppercase text-emerald-300 font-semibold">{t("matches")}</div>
              <div className="text-2xl font-display font-bold tabular-nums text-emerald-300" data-testid="rc-matches">{scannedExpectedCodes.size}</div>
            </div>
            <div className={`${extras.length > 0 ? "bg-amber-900/40 border-amber-700/40" : "bg-slate-800 border-slate-700"} border rounded-lg p-3`}>
              <div className="text-[10px] uppercase text-amber-300 font-semibold">{t("extras")}</div>
              <div className="text-2xl font-display font-bold tabular-nums text-amber-300" data-testid="rc-extras">{extras.length}</div>
            </div>
          </div>

          <ScannerInput dark
            testIdPrefix="scan-package"
            placeholder={t("scan_hint")}
            onScan={scanPkg}
          />

          {/* Expected list */}
          <div className="bg-slate-800/50 rounded-lg p-2 max-h-64 overflow-y-auto">
            <div className="text-[10px] uppercase text-slate-400 font-semibold mb-1 px-1">{t("expected")} · {session.expected.length}</div>
            {session.expected.map((e) => {
              const ok = scannedExpectedCodes.has(e.tracking_code);
              return (
                <div key={e.tracking_code} className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs ${ok ? "bg-emerald-900/40" : "bg-slate-800/60"}`} data-testid={`exp-${e.tracking_code}`}>
                  {ok ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Package className="w-4 h-4 text-slate-500" />}
                  <span className="font-mono flex-1 truncate">{e.tracking_code}</span>
                  <span className="text-slate-400 text-[10px] truncate">{e.descripcion || `${e.origen} → ${e.destino}`}</span>
                </div>
              );
            })}
          </div>

          {/* Extras list */}
          {extras.length > 0 && (
            <div className="bg-amber-900/20 border border-amber-700/40 rounded-lg p-2">
              <div className="text-[10px] uppercase text-amber-300 font-semibold mb-1 px-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {t("extras")}</div>
              {extras.map(e => (
                <div key={e.tracking_code} className="flex items-center gap-2 px-2 py-1 text-xs" data-testid={`extra-${e.tracking_code}`}>
                  <FileWarning className="w-3 h-3 text-amber-400" />
                  <span className="font-mono">{e.tracking_code}</span>
                </div>
              ))}
            </div>
          )}

          {/* Close step */}
          <div className="border-t border-slate-700 pt-3 space-y-2">
            <div className="text-[10px] uppercase text-slate-400 font-semibold">{t("step_3_close_reception")}</div>
            <input
              value={nota} onChange={(e) => setNota(e.target.value)}
              placeholder={t("note_optional")}
              className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 outline-none focus:border-emerald-400 text-sm"
              data-testid="rc-nota"
            />
            {(missingList.length > 0 || extras.length > 0) && (
              <input
                value={photoUrl} onChange={(e) => setPhotoUrl(e.target.value)}
                placeholder={t("photo_url")}
                className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 outline-none focus:border-emerald-400 text-sm"
                data-testid="rc-photo"
              />
            )}
            <button
              onClick={closeSession}
              disabled={busy}
              data-testid="rc-close"
              className={`w-full py-3 rounded font-semibold text-sm flex items-center justify-center gap-2 ${(missingList.length > 0 || extras.length > 0) ? "bg-amber-500 hover:bg-amber-600" : "bg-emerald-500 hover:bg-emerald-600"} text-white`}
            >
              <CheckCircle2 className="w-4 h-4" />
              {t("close_reception")}
              {missingList.length > 0 && <span className="ms-2 bg-black/30 rounded-full px-2 text-xs">−{missingList.length}</span>}
              {extras.length > 0 && <span className="ms-2 bg-black/30 rounded-full px-2 text-xs">+{extras.length}</span>}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: summary */}
      {closeResult && (
        <div className="space-y-3" data-testid="reception-summary">
          <div className="text-emerald-400 font-semibold flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5" /> {t("reception_summary")}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <SummaryCard label={t("matches")} value={closeResult.received} color="emerald" />
            <SummaryCard label={t("missing")} value={closeResult.missing_count} color="red" />
            <SummaryCard label={t("extras")} value={closeResult.extra_count} color="amber" />
            <SummaryCard label={t("incidents_created")} value={closeResult.incidents_created} color="purple" />
          </div>
          <button onClick={reset} data-testid="new-reception-btn" className="w-full py-2 rounded bg-slate-700 hover:bg-slate-600 text-white font-semibold text-sm">{t("open_reception")}</button>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, color }) {
  const colors = {
    emerald: "bg-emerald-900/40 border-emerald-700/40 text-emerald-300",
    red: "bg-red-900/40 border-red-700/40 text-red-300",
    amber: "bg-amber-900/40 border-amber-700/40 text-amber-300",
    purple: "bg-purple-900/40 border-purple-700/40 text-purple-300",
  };
  return (
    <div className={`rounded-lg p-3 border ${colors[color]}`}>
      <div className="text-[10px] uppercase font-semibold opacity-80">{label}</div>
      <div className="text-2xl font-display font-bold tabular-nums">{value}</div>
    </div>
  );
}

/* =========================================================================
   RECEIVE DIRECT (no manifest)
   ========================================================================= */
function ReceiveDirectPanel({ api, user, clients, warehouses, t, onDone }) {
  const [scanned, setScanned] = useState(null); // {tracking_code, ...} shipment info
  const [preview, setPreview] = useState(null); // label/shipment preview before confirming entry
  const [quickForm, setQuickForm] = useState(null); // when unknown package
  const [busy, setBusy] = useState(false);
  const [warehouseId, setWarehouseId] = useState(user.warehouse_id || warehouses[0]?.id || "");
  const [ubicacion, setUbicacion] = useState("");

  useEffect(() => {
    if (!warehouseId) setWarehouseId(user.warehouse_id || warehouses[0]?.id || "");
  }, [warehouses, user, warehouseId]);

  const handleScan = async (code) => {
    setBusy(true);
    setScanned(null);
    try {
      // Look up the label/shipment first and show its data so the operator can confirm entry
      const { data } = await api.get(`/inventory/lookup`, { params: { code } });
      setPreview({
        ...data,
        peso_recibido: data.peso,
        unidades_recibidas: data.unidades,
      });
    } catch (err) {
      if (err.response?.status === 404) {
        // Unknown code → offer quick create
        setQuickForm({ tracking_code: code, client_id: "", peso: 0, cbm: 0, descripcion: "", destino: "" });
      } else {
        toast.error(err.response?.data?.detail || "Error");
      }
    } finally { setBusy(false); }
  };

  const confirmReceive = async () => {
    if (!warehouseId) return toast.error(t("warehouse"));
    setBusy(true);
    try {
      const { data } = await api.post(`/inventory/receive-direct`, {
        tracking_code: preview.codigo,
        warehouse_id: warehouseId,
        ubicacion,
        peso: +preview.peso_recibido || 0,
        unidades: +preview.unidades_recibidas || 0,
        cbm: +preview.cbm || 0,
      });
      setScanned({ tracking_code: preview.codigo, estado: "recibido", descripcion: preview.contenido, ...data });
      toast.success(`✓ ${t("package_ok")}: ${preview.codigo}`);
      setPreview(null);
      setUbicacion("");
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  const submitQuick = async () => {
    if (!quickForm.client_id) return toast.error(t("client_required_new_pkg"));
    setBusy(true);
    try {
      const { data } = await api.post(`/inventory/receive-direct`, {
        ...quickForm, warehouse_id: warehouseId, ubicacion,
      });
      toast.success(`✓ ${t("saved")}: ${data.shipment.tracking_code}`);
      setScanned(data.shipment);
      setQuickForm(null);
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-slate-900 text-white rounded-xl p-4 space-y-3" data-testid="panel-receive-direct">
      <div className="flex items-center gap-2">
        <PackagePlus className="w-5 h-5 text-blue-400" />
        <div>
          <div className="font-semibold text-sm">{t("mode_receive_direct")}</div>
          <div className="text-xs text-slate-400">{t("mode_receive_direct_desc")}</div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] uppercase text-slate-400 font-semibold">{t("warehouse")}</label>
          <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-white text-sm" data-testid="rd-wh">
            <option value="">—</option>
            {warehouses.map(w => <option key={w.id} value={w.id}>{w.nombre}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase text-slate-400 font-semibold">{t("location")}</label>
          <input value={ubicacion} onChange={(e) => setUbicacion(e.target.value)} placeholder="MADRID-A01-P03" className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 text-sm" data-testid="rd-loc" />
        </div>
      </div>

      <ScannerInput dark
        testIdPrefix="rd"
        placeholder={t("scan_hint")}
        onScan={handleScan}
      />

      {preview && (
        <div className="bg-slate-800 border border-blue-600/50 rounded-lg p-4 space-y-3" data-testid="rd-preview">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${preview.tipo === "etiqueta" ? "bg-indigo-500/30 text-indigo-200" : "bg-emerald-500/30 text-emerald-200"}`}>{preview.tipo === "etiqueta" ? t("label") : t("shipment")}</span>
              <span className="font-mono font-bold">{preview.codigo}</span>
            </div>
            <button onClick={() => setPreview(null)} data-testid="rd-preview-cancel"><X className="w-4 h-4 text-slate-400" /></button>
          </div>
          {preview.ya_recibida && <div className="text-[11px] text-amber-300 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {t("already_received")}</div>}
          <div className="text-xs text-slate-300 space-y-0.5">
            {preview.contenido && <div><span className="text-slate-500">{t("content")}:</span> {preview.contenido}</div>}
            {(preview.remitente || preview.receptor) && <div><span className="text-slate-500">{t("sender")} → {t("recipient")}:</span> {preview.remitente || "—"} → {preview.receptor || "—"}</div>}
            {preview.modo && <div><span className="text-slate-500">{t("transport_mode")}:</span> {t(preview.modo === "maritimo" ? "sea" : preview.modo === "aereo" ? "air" : "road")}</div>}
            <div><span className="text-slate-500">{t("expected")}:</span> {preview.unidades} und · {preview.peso} kg</div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase text-slate-400 font-semibold">{t("units")} ({t("received")})</label>
              <input type="number" min={1} value={preview.unidades_recibidas} onChange={(e) => setPreview({ ...preview, unidades_recibidas: +e.target.value })} className="w-full px-3 py-2 rounded bg-slate-900 border border-slate-700 text-white text-sm" data-testid="rd-preview-unidades" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-slate-400 font-semibold">{t("weight_kg")} ({t("received")})</label>
              <input type="number" step="0.1" value={preview.peso_recibido} onChange={(e) => setPreview({ ...preview, peso_recibido: +e.target.value })} className="w-full px-3 py-2 rounded bg-slate-900 border border-slate-700 text-white text-sm" data-testid="rd-preview-peso" />
            </div>
          </div>
          <button onClick={confirmReceive} disabled={busy} data-testid="rd-preview-confirm" className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2">
            <ArrowDownToLine className="w-4 h-4" /> {busy ? "…" : t("give_entry")}
          </button>
        </div>
      )}

      {scanned && (
        <div className="bg-emerald-900/40 border border-emerald-700 rounded-lg p-3 text-sm" data-testid="rd-last">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            <span className="font-mono font-bold">{scanned.tracking_code}</span>
          </div>
          <div className="text-xs text-slate-300">{scanned.descripcion || "—"}{scanned.origen || scanned.destino ? ` · ${scanned.origen || "—"} → ${scanned.destino || "—"}` : ""}{scanned.fecha_recepcion ? ` · ${new Date(scanned.fecha_recepcion).toLocaleString()}` : ""}</div>
        </div>
      )}

      {quickForm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white text-slate-900 rounded-xl w-full max-w-lg p-6 space-y-3" data-testid="quick-create-modal">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold text-lg flex items-center gap-2"><PackagePlus className="w-5 h-5 text-blue-600" /> {t("quick_create_shipment")}</h3>
              <button onClick={() => setQuickForm(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs text-amber-800">
              <AlertTriangle className="w-3 h-3 inline me-1" /> {t("unknown_package")}: <span className="font-mono font-bold">{quickForm.tracking_code}</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2">
                <label className="text-[11px] font-semibold text-slate-600 uppercase mb-1 block">{t("clients")} *</label>
                <select value={quickForm.client_id} onChange={(e) => setQuickForm({ ...quickForm, client_id: e.target.value })} required className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="qc-client">
                  <option value="">— {t("select")} —</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.fullname} · {c.email}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-600 uppercase mb-1 block">{t("weight_kg")}</label>
                <input type="number" step="0.1" value={quickForm.peso} onChange={(e) => setQuickForm({ ...quickForm, peso: +e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="qc-peso" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-600 uppercase mb-1 block">CBM</label>
                <input type="number" step="0.01" value={quickForm.cbm} onChange={(e) => setQuickForm({ ...quickForm, cbm: +e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
              </div>
              <div className="col-span-2">
                <label className="text-[11px] font-semibold text-slate-600 uppercase mb-1 block">{t("description")}</label>
                <input value={quickForm.descripcion} onChange={(e) => setQuickForm({ ...quickForm, descripcion: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="qc-desc" />
              </div>
              <div className="col-span-2">
                <label className="text-[11px] font-semibold text-slate-600 uppercase mb-1 block">{t("destination")}</label>
                <input value={quickForm.destino} onChange={(e) => setQuickForm({ ...quickForm, destino: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setQuickForm(null)} className="flex-1 border border-slate-300 py-2.5 rounded-lg font-semibold text-sm">{t("cancel")}</button>
              <button onClick={submitQuick} disabled={busy} data-testid="qc-submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-lg font-semibold text-sm">{t("save")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================================================================
   PREPARE EXIT (scan → auto manifest)
   ========================================================================= */
function PrepareExitPanel({ api, user, t, onDone }) {
  const [codes, setCodes] = useState([]); // scanned tracking codes
  const [nombre, setNombre] = useState("");
  const [destino, setDestino] = useState("");
  const [modo, setModo] = useState("terrestre");
  const [container, setContainer] = useState("");
  const [voyageId, setVoyageId] = useState("");
  const [voyages, setVoyages] = useState([]);
  const [vehiculo, setVehiculo] = useState("");
  const [conductor, setConductor] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/voyages").then(r => setVoyages(r.data)).catch(() => {}); }, [api]);

  const addCode = (code) => {
    if (codes.includes(code)) {
      toast.info(t("package_duplicate"));
      return;
    }
    setCodes([...codes, code]);
    toast.success(code);
  };

  const removeCode = (code) => setCodes(codes.filter(c => c !== code));

  const generate = async () => {
    if (codes.length === 0) return toast.error(t("no_inventory"));
    if (!nombre.trim()) return toast.error(t("name"));
    setBusy(true);
    try {
      const { data } = await api.post("/manifests/auto-exit", {
        nombre, destino, modo, container_number: container, voyage_id: voyageId,
        vehiculo, conductor, tracking_codes: codes,
      });
      setResult(data);
      toast.success(`✓ ${data.manifest.codigo}`);
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  const reset = () => {
    setCodes([]); setNombre(""); setDestino(""); setContainer(""); setVoyageId("");
    setVehiculo(""); setConductor(""); setResult(null);
  };

  return (
    <div className="bg-slate-900 text-white rounded-xl p-4 space-y-3" data-testid="panel-prepare-exit">
      <div className="flex items-center gap-2">
        <Truck className="w-5 h-5 text-purple-400" />
        <div>
          <div className="font-semibold text-sm">{t("mode_prepare_exit")}</div>
          <div className="text-xs text-slate-400">{t("mode_prepare_exit_desc")}</div>
        </div>
      </div>

      {!result && (
        <>
          {/* Meta */}
          <div className="grid grid-cols-2 gap-2">
            <input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder={t("name") + " *"} className="col-span-2 px-3 py-2 rounded bg-slate-800 border border-slate-700 text-white text-sm" data-testid="pe-name" />
            <input value={destino} onChange={(e) => setDestino(e.target.value)} placeholder={t("exit_destination")} className="px-3 py-2 rounded bg-slate-800 border border-slate-700 text-white text-sm" data-testid="pe-dest" />
            <select value={modo} onChange={(e) => setModo(e.target.value)} className="px-3 py-2 rounded bg-slate-800 border border-slate-700 text-white text-sm" data-testid="pe-modo">
              <option value="terrestre">{t("ground")}</option>
              <option value="aereo">{t("air")}</option>
              <option value="maritimo">{t("sea")}</option>
            </select>
            <input value={vehiculo} onChange={(e) => setVehiculo(e.target.value)} placeholder={t("vehicle")} className="px-3 py-2 rounded bg-slate-800 border border-slate-700 text-white text-sm" />
            <input value={conductor} onChange={(e) => setConductor(e.target.value)} placeholder={t("driver")} className="px-3 py-2 rounded bg-slate-800 border border-slate-700 text-white text-sm" />
            {modo === "maritimo" && (
              <>
                <input value={container} onChange={(e) => setContainer(e.target.value)} placeholder={t("container_number")} className="px-3 py-2 rounded bg-slate-800 border border-slate-700 text-white text-sm" />
                <select value={voyageId} onChange={(e) => setVoyageId(e.target.value)} className="px-3 py-2 rounded bg-slate-800 border border-slate-700 text-white text-sm">
                  <option value="">{t("voyage")} —</option>
                  {voyages.filter(v => v.activa).map(v => <option key={v.id} value={v.id}>{v.codigo} · {v.puerto_origen} → {v.puerto_destino}</option>)}
                </select>
              </>
            )}
          </div>

          {/* Scanner */}
          <ScannerInput dark testIdPrefix="pe" placeholder={t("scan_hint")} onScan={addCode} />

          {/* Scanned list */}
          <div className="bg-slate-800/50 rounded-lg p-2 max-h-64 overflow-y-auto">
            <div className="text-[10px] uppercase text-slate-400 font-semibold mb-1 px-1 flex justify-between">
              <span>{t("scanned_packages")}</span>
              <span className="tabular-nums" data-testid="pe-count">{codes.length}</span>
            </div>
            {codes.length === 0 && <p className="text-xs text-slate-500 italic p-2">—</p>}
            {codes.map((c) => (
              <div key={c} className="flex items-center gap-2 px-2 py-1.5 bg-slate-800 rounded text-xs mb-1" data-testid={`pe-code-${c}`}>
                <CheckCircle2 className="w-3 h-3 text-purple-400" />
                <span className="font-mono flex-1">{c}</span>
                <button onClick={() => removeCode(c)} className="text-slate-400 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
              </div>
            ))}
          </div>

          <button
            onClick={generate}
            disabled={busy || codes.length === 0 || !nombre.trim()}
            data-testid="pe-generate"
            className="w-full py-3 rounded bg-purple-500 hover:bg-purple-600 disabled:bg-slate-700 disabled:text-slate-500 text-white font-semibold text-sm flex items-center justify-center gap-2"
          >
            <Truck className="w-4 h-4" /> {t("generate_exit_manifest")} ({codes.length})
          </button>
        </>
      )}

      {result && (
        <div className="space-y-3" data-testid="pe-result">
          <div className="text-emerald-400 font-semibold flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5" /> {result.manifest.codigo}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <SummaryCard label={t("scanned")} value={codes.length} color="emerald" />
            <SummaryCard label={t("not_found")} value={result.not_found.length} color="red" />
          </div>
          {result.not_found.length > 0 && (
            <div className="bg-red-900/30 border border-red-700 rounded p-2">
              <div className="text-[10px] uppercase text-red-300 font-semibold mb-1">{t("not_found")}</div>
              {result.not_found.map(nf => (
                <div key={nf} className="font-mono text-xs text-red-200">{nf}</div>
              ))}
            </div>
          )}
          <button onClick={reset} className="w-full py-2 rounded bg-slate-700 hover:bg-slate-600 text-white font-semibold text-sm">{t("new")}</button>
        </div>
      )}
    </div>
  );
}
